import { JSX, useCallback, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import JSelectDisplayOption from "$components/JSelectDisplayOption/JSelectDisplayOption";
import GenericSelect, { GenericSelectProps } from "./GenericSelect";
import { normalizeText } from "$utils/utils";

type Props = {
  value: any;
  onChange: (id, value) => void;
  multiple?: boolean;
  required?: boolean;
  disabled?: boolean;
  disableClearable?: boolean;
  valueKey?: string;
  label?: string;
  placeholder?: string;
  error?: boolean;
  errorText?: string;
  noOptionHint?: string;
  rowsPerPage?: number;
  minCharsToTrigerSearch?: number;
  queryKey: any[];
  queryFn: (filters) => Promise<any>;
  queryfilters: any;
  debouncedInputValue: string;
  inputValue: string;
  setInputValue: (value: string) => void;
  localOptions: any[];
  setLocalOptions: (value: any) => void;
  renderOption?: (value: any) => JSX.Element;
  labelKey?: string;
  labelKey2?: string;
  queryFieldForInit?: string;
  createLabel?: string;
  filterIds?: string[];
  shouldResetValueOnBlur?: boolean;
  onDataFetched?: (options: any[]) => void;
  enabled?: boolean;
  expandOptionsWhenSelected?: boolean;
};

const SearchSelect = ({
  value,
  onChange,
  multiple,
  required,
  disabled,
  valueKey = "_id",
  label,
  placeholder,
  minCharsToTrigerSearch = 2,
  errorText,
  disableClearable,
  noOptionHint = "Pas de résultat correspondant...",
  queryKey,
  queryFn,
  queryfilters,
  debouncedInputValue,
  inputValue,
  setInputValue,
  enabled = true,
  onDataFetched,
  localOptions,
  setLocalOptions,
  labelKey = "name",
  labelKey2 = "code",
  filterIds = [],
  queryFieldForInit = "$inId",
  createLabel,
  shouldResetValueOnBlur = true,
  expandOptionsWhenSelected = false,
  renderOption,
  ...props
}: Props & Partial<GenericSelectProps>) => {
  const getOptionLabel = useCallback(
    (opt) => `${opt?.[labelKey]}${labelKey2 && ` - [${opt?.[labelKey2]}]`}`,
    [labelKey, labelKey2]
  );
  const defaultRenderOption = (opt: any) => (
    <JSelectDisplayOption value1={opt?.[labelKey]} value2={opt?.[labelKey2]} />
  );

  const [noOptionsText, setNoOptionsText] = useState<string>(placeholder);
  const [placeholderText, setPlaceholderText] = useState<string>(placeholder);
  const [options, setOptions] = useState<any[]>([]);

  const queryClient = useQueryClient();

  // Helper function to initialize options from value
  const initFromValue = useCallback(
    async (currentValue: any) => {
      if (
        (multiple && currentValue?.length > 0) ||
        (!multiple && !!currentValue)
      ) {
        const idFilter = Array.isArray(currentValue)
          ? currentValue
          : [currentValue];
        const queryParams = {
          [queryFieldForInit]: idFilter,
        };

        try {
          // Helper function to find cached data
          const findCachedData = (): any => {
            // First, try exact match with normalized key (fastest path)
            const sortedIdFilter = Array.isArray(idFilter)
              ? [...idFilter].sort()
              : idFilter;
            const normalizedQueryParams = {
              [queryFieldForInit]: sortedIdFilter,
            };
            const cacheKey = [queryKey[0], normalizedQueryParams];
            const exactMatch = queryClient.getQueryData(cacheKey);
            if (exactMatch) {
              return exactMatch;
            }

            // If exact match not found, search for queries that contain our idFilter
            // Only check queries that have the same action prefix and queryFieldForInit field
            const queryCache = queryClient.getQueryCache();
            const idFilterSet = new Set(idFilter.map(String));

            // Get all queries but filter early to only check relevant ones
            // We only check queries where the cache key structure matches our pattern
            const allQueries = queryCache.getAll();
            const matchingQuery = allQueries.find((query) => {
              const key = query.queryKey;

              // Early return: Must start with the same action
              if (key[0] !== queryKey[0]) return false;

              // Early return: Must have at least 2 elements (action + params)
              if (key.length < 2) return false;

              const keyParams = key[1] as any;

              // Early return: Second element must be an object
              if (!keyParams || typeof keyParams !== "object") return false;

              // Early return: Must have the queryFieldForInit field (e.g., $inId, $inreference)
              const keyFilter = keyParams[queryFieldForInit];
              if (!keyFilter || !Array.isArray(keyFilter)) return false;

              // Now check if the arrays contain the same IDs (order doesn't matter)
              const keyFilterSet = new Set(keyFilter.map(String));

              // Both sets must have the same size and contain the same IDs
              if (keyFilterSet.size !== idFilterSet.size) return false;
              // Check if all IDs in idFilter are present in keyFilterSet
              return idFilter.every((id) => keyFilterSet.has(String(id)));
            });

            if (matchingQuery && matchingQuery.state.data) {
              return matchingQuery.state.data;
            }

            return null;
          };

          // Try to find cached data
          let initResult: any = findCachedData();

          if (!initResult) {
            // If not in cache after waiting, make the API call
            initResult = await queryFn(queryParams);

            // Cache the result for future use with normalized key
            const sortedIdFilter = Array.isArray(idFilter)
              ? [...idFilter].sort()
              : idFilter;
            const normalizedQueryParams = {
              [queryFieldForInit]: sortedIdFilter,
            };
            const cacheKey = [queryKey[0], normalizedQueryParams];
            queryClient.setQueryData(cacheKey, initResult);
          }

          let nextOptions = [...(initResult?.data || [])];

          // Special case for products...
          if (nextOptions.length === 0 && Array.isArray(initResult)) {
            nextOptions = initResult;
          }

          if (nextOptions?.length > 0) {
            // Check if the current value is already in localOptions
            const valueInLocalOptions = localOptions.some((opt) => {
              if (multiple) {
                return (
                  Array.isArray(currentValue) &&
                  currentValue.includes(opt[valueKey])
                );
              }
              return opt[valueKey] === currentValue;
            });

            // Only update if the value is not already in localOptions
            if (!valueInLocalOptions) {
              setLocalOptions(nextOptions);
            }
          }
        } catch (error) {
          console.error("❌ SearchSelect init failed:", error);
          console.error("❌ Failed query params:", queryParams);
        }
      }
    },
    [
      multiple,
      queryFieldForInit,
      queryKey,
      queryFn,
      queryClient,
      localOptions,
      valueKey,
      setLocalOptions,
    ]
  );

  // Init (OnMount)
  // when the select as already a value --> gets it's value
  useEffect(() => {
    initFromValue(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only to get the initial value --> Do NOT add dependencies

  // Watch for value changes and re-initialize if needed
  useEffect(() => {
    if ((multiple && value?.length > 0) || (!multiple && !!value)) {
      // Check if the current value is already in localOptions
      const valueInLocalOptions = localOptions.some((opt) => {
        if (multiple) {
          return Array.isArray(value) && value.some((v) => opt[valueKey] === v);
        }
        return opt[valueKey] === value;
      });

      // If value is not in localOptions, re-initialize
      if (!valueInLocalOptions) {
        initFromValue(value);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, multiple, valueKey]);

  const { data: dataResponse, isLoading } = useQuery({
    queryKey,
    queryFn: () => queryFn(queryfilters),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: enabled && debouncedInputValue?.length >= minCharsToTrigerSearch,
    gcTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    const shouldShowSearchResults =
      debouncedInputValue?.length >= minCharsToTrigerSearch && enabled;
    const hasCurrentValue =
      (multiple && value?.length > 0) || (!multiple && !!value);

    // Check if the current debouncedInputValue exactly matches any of our local options
    const isInputMatchingLocalOption = localOptions?.some((opt) => {
      const optLabel = getOptionLabel(opt);
      // Check various possible matches
      return (
        optLabel === debouncedInputValue ||
        opt[labelKey] === debouncedInputValue ||
        opt[labelKey2] === debouncedInputValue ||
        opt[valueKey] === debouncedInputValue
      );
    });

    // Opt-in (catalogue): treat the selected label as display, not as a search,
    // so reopening the dropdown keeps the full company list.
    const selectedOption =
      expandOptionsWhenSelected && !multiple && value
        ? localOptions?.find((opt) => opt?.[valueKey] === value)
        : undefined;
    const selectedLabel = selectedOption ? getOptionLabel(selectedOption) : "";
    const inputDisplaysSelection =
      expandOptionsWhenSelected &&
      !!selectedLabel &&
      (inputValue === selectedLabel || debouncedInputValue === selectedLabel);

    // If disabled (companyOptions provided), only use local options
    if (!enabled && localOptions.length > 0) {
      let nextOptions = [...localOptions];

      // Filter local options based on input if there's a search term.
      // With expandOptionsWhenSelected, skip filtering while the field still
      // shows the selected label (otherwise only that option remains).
      if (
        debouncedInputValue &&
        debouncedInputValue.length > 0 &&
        !inputDisplaysSelection
      ) {
        nextOptions = localOptions.filter((opt) => {
          const optLabel = getOptionLabel(opt);
          return (
            optLabel
              .toLowerCase()
              .includes(debouncedInputValue.toLowerCase()) ||
            opt[labelKey]
              ?.toLowerCase()
              .includes(debouncedInputValue.toLowerCase()) ||
            opt[labelKey2]
              ?.toLowerCase()
              .includes(debouncedInputValue.toLowerCase())
          );
        });
      }

      setOptions(nextOptions);
      if (onDataFetched) {
        onDataFetched(nextOptions);
      }
      return;
    }

    if (hasCurrentValue && localOptions && localOptions.length > 0) {
      let nextOptions = [...localOptions];

      // Default: hide server results when the input matches the selected option.
      // expandOptionsWhenSelected: keep them so the full list stays available.
      if (
        shouldShowSearchResults &&
        (!isInputMatchingLocalOption || inputDisplaysSelection)
      ) {
        let serverOptions = [...(dataResponse?.data || [])];

        // Special case for products...
        if (serverOptions.length === 0 && Array.isArray(dataResponse)) {
          serverOptions = dataResponse;
        }

        // Filter out duplicates from server response
        const uniqueServerOptions = serverOptions.filter((o) =>
          localOptions.every(
            (localOpt) => localOpt?.[valueKey] !== o?.[valueKey]
          )
        );

        nextOptions = [...uniqueServerOptions, ...localOptions];

        const total = dataResponse?.total || 0;
        const hasRowsPerPageFilter =
          "rowsPerPage" in queryfilters && queryfilters.rowsPerPage;
        if (hasRowsPerPageFilter && total > queryfilters.rowsPerPage) {
          nextOptions.push({
            _id: "hasMoreResult",
            name: `${
              total - queryfilters.rowsPerPage
            } résultats correspondants supplémentaires...`,
            value: "disabled",
            disabled: true,
          });
        }

        // Create option if none of the current match the search
        const shouldPrintCreate = !nextOptions.some(
          (opt) =>
            normalizeText(opt?.[labelKey]) ===
              normalizeText(debouncedInputValue) ||
            normalizeText(opt?.[labelKey2]) ===
              normalizeText(debouncedInputValue)
        );
        if (
          createLabel?.length > 1 &&
          shouldPrintCreate &&
          debouncedInputValue.length > 0
        ) {
          nextOptions.push({
            _id: "create",
            [labelKey]: `${createLabel} "${debouncedInputValue}"`,
            [valueKey]: debouncedInputValue,
            inputValue: "create",
          });
        }
      }

      setOptions(nextOptions);
      if (onDataFetched) {
        onDataFetched(nextOptions);
      }
    } else if (shouldShowSearchResults) {
      // No current value but we should show search results
      let nextOptions = [...(dataResponse?.data || [])];

      // Special case for products...
      if (nextOptions.length === 0 && Array.isArray(dataResponse)) {
        nextOptions = dataResponse;
      }

      const total = dataResponse?.total || 0;
      const hasRowsPerPageFilter =
        "rowsPerPage" in queryfilters && queryfilters.rowsPerPage;
      if (hasRowsPerPageFilter && total > queryfilters.rowsPerPage) {
        // Adds a special disabled option with text to hint that there's more results
        nextOptions.push({
          _id: "hasMoreResult",
          name: `${
            total - queryfilters.rowsPerPage
          } résultats correspondants supplémentaires...`,
          value: "disabled",
          disabled: true,
        });
      }

      // Create option if none of the current match the search:
      const shouldPrintCreate = !nextOptions.some(
        (opt) =>
          normalizeText(opt?.[labelKey]) ===
            normalizeText(debouncedInputValue) ||
          normalizeText(opt?.[labelKey2]) === normalizeText(debouncedInputValue)
      );
      if (
        createLabel?.length > 1 &&
        shouldPrintCreate &&
        debouncedInputValue.length > 0
      ) {
        nextOptions.push({
          _id: "create",
          [labelKey]: `${createLabel} "${debouncedInputValue}"`,
          [valueKey]: debouncedInputValue,
          inputValue: "create",
        });
      }

      setOptions(nextOptions);
      if (onDataFetched) {
        onDataFetched(nextOptions);
      }
    } else {
      // No search query and no value - show empty options
      setOptions([]);
      if (onDataFetched) {
        onDataFetched([]);
      }
    }
  }, [
    onDataFetched,
    dataResponse,
    queryfilters,
    localOptions,
    valueKey,
    createLabel,
    debouncedInputValue,
    labelKey,
    labelKey2,
    minCharsToTrigerSearch,
    multiple,
    value,
    inputValue,
    getOptionLabel,
    enabled,
    expandOptionsWhenSelected,
  ]);

  // Change no option text and placeholder
  useEffect(() => {
    const hasCurrentValue =
      (multiple && value?.length > 0) || (!multiple && !!value);

    if (inputValue?.length < minCharsToTrigerSearch) {
      // If we have a current value but input is below minimum, don't show the "enter X characters" message
      if (hasCurrentValue && localOptions.length > 0) {
        setNoOptionsText(noOptionHint);
        setPlaceholderText(placeholder);
      } else {
        setNoOptionsText(
          `Entrer au moins ${
            minCharsToTrigerSearch === 0 ? "1" : minCharsToTrigerSearch
          } caractère${minCharsToTrigerSearch > 1 ? "s" : ""}`
        );
        setPlaceholderText(placeholder);
      }
      return;
    }

    // Only show "no options" message if there are truly no options from the server
    // Don't consider local selected options in this check
    const hasServerOptions = options?.some(
      (opt) =>
        opt._id !== "hasMoreResult" &&
        opt._id !== "create" &&
        !opt.disabled &&
        !localOptions?.some((localOpt) => localOpt[valueKey] === opt[valueKey])
    );

    if (
      !hasServerOptions &&
      debouncedInputValue?.length >= minCharsToTrigerSearch &&
      (!hasCurrentValue || localOptions?.length === 0)
    ) {
      setNoOptionsText(noOptionHint);
      setPlaceholderText(noOptionHint);
    } else {
      setPlaceholderText(placeholder);
      setNoOptionsText(noOptionHint);
    }
  }, [
    inputValue?.length,
    minCharsToTrigerSearch,
    options,
    placeholder,
    noOptionHint,
    debouncedInputValue?.length,
    value,
    localOptions,
    multiple,
    valueKey,
  ]);

  const handleChange = (selectedValue: any, selectedOption: any) => {
    if (selectedOption) {
      if (multiple) {
        setInputValue(""); // reset search on select
        setLocalOptions(selectedOption);
      } else {
        setInputValue(getOptionLabel(selectedOption));
        setLocalOptions([selectedOption]);
      }
    } else {
      setLocalOptions([]);
      setInputValue("");
    }

    // Modifie la valeur dans le composant parent (plus généralement dans le formulaire)
    if (onChange) {
      onChange(selectedValue, selectedOption);
    }
  };

  const onBlur = useCallback(() => {
    if (!!value && inputValue && !multiple && shouldResetValueOnBlur) {
      const selectedOption = options.find((o) => o[valueKey] === value);
      setInputValue(getOptionLabel(selectedOption));
    }
  }, [
    value,
    inputValue,
    multiple,
    shouldResetValueOnBlur,
    options,
    setInputValue,
    getOptionLabel,
    valueKey,
  ]);

  return (
    <GenericSelect
      disableClearable={disableClearable}
      required={required}
      multiple={multiple}
      value={value}
      options={options.filter((opt: any) => !filterIds.includes(opt._id))}
      inputValue={inputValue}
      loading={isLoading}
      valueKey={valueKey}
      label={label}
      noOptionsText={noOptionsText}
      placeholder={placeholderText}
      onInputChange={setInputValue}
      onChange={handleChange}
      onBlur={onBlur}
      disabled={disabled}
      getOptionLabel={getOptionLabel}
      blurOnSelect
      filterSelectedOptions={false}
      getOptionDisabled={(option) => option.disabled === true}
      filterOptions={(opt) => opt}
      clearOnBlur={multiple ? value?.length > 0 : !!value}
      renderOption={renderOption || defaultRenderOption}
      error={!!errorText}
      errorText={errorText}
      {...props}
    />
  );
};

export default SearchSelect;
